import React, { useCallback, useEffect } from 'react';
import { Paragraph4 } from '@nexus/lib-react/dist/core/Typography';
import S from '../style/Dac7TabContent';
import { Dac7BusinessAddress } from '../../../../../../typings/dac7';
import { useI18n } from '../../../../../i18n';
import Dac7Dropdown from './Dac7Dropdown';
import useDac7ArrayItems from '../hooks/useDac7ArrayItems';
import Dac7RemoveItemButton from './Dac7RemoveItemButton';
import Dac7AddItemButton from './Dac7AddItemButton';
import countriesListEU from '../utils/countriesListEU';

interface Props {
  permanentEstablishmentCountries: Readonly<Dac7BusinessAddress['establishmentCountries']>;
  frozenPermanentEstablishmentCountries: Readonly<Dac7BusinessAddress['establishmentCountries']>;
  onChange?: (index: number, value: string) => void;
}

const Dac7PermanentEstablishment = ({
  permanentEstablishmentCountries: initialCountries,
  frozenPermanentEstablishmentCountries,
  onChange,
}: Props) => {
  const i18n = useI18n();
  const {
    items: permanentEstablishmentCountries,
    addItem,
    removeItem,
  } = useDac7ArrayItems(initialCountries);

  const addPermanentEstablishment = useCallback(() => {
    addItem({ countryCode: '' });
  }, [addItem]);

  const removePermanentEstablishment = useCallback(
    i => {
      removeItem(i);
    },
    [removeItem],
  );

  useEffect(() => {
    // add by default at least one permanent establishment if none
    if (initialCountries.length === 0) {
      addPermanentEstablishment();
    }
  }, [initialCountries, addPermanentEstablishment]);

  return (
    <>
      {permanentEstablishmentCountries.map((establishment, i) => (
        <S.Dac7FormField key={establishment.feId}>
          <Dac7Dropdown
            sourceItems={countriesListEU}
            initialSelectedIds={[establishment?.countryCode]}
            name={`dac7_address.establishmentCountries.${i}.countryCode`}
            label={`${i18n('dlv_dac7_permanentEstablishmentCountry')}${
              i === 0 ? '' : ` #${i + 1}`
            }`}
            required
            requiredMessage={i18n('dlv_dac7_permanentEstablishmentCountryRequired')}
            placeholder={i18n('dlv_dac7_permanentEstablishmentCountryPlaceholder')}
            disabled={!!frozenPermanentEstablishmentCountries[i]?.countryCode}
            onChange={([countryCode]) => {
              onChange?.(i, String(countryCode));
            }}
          />
          <Paragraph4 mt={12} color={permanentEstablishmentCountries[i] ? '#7F9799' : '#002F34'}>
            {i18n('dlv_dac7_permanentEstablishmentCountry_description')}
          </Paragraph4>

          {i === 0 || !!establishment.countryCode ? null : (
            <Dac7RemoveItemButton
              onClick={() => removePermanentEstablishment(i)}
              label={i18n('dlv_dac7_remove_item_label')}
            />
          )}
        </S.Dac7FormField>
      ))}
      <Dac7AddItemButton
        onClick={addPermanentEstablishment}
        label={i18n('dlv_dac7_permanentEstablishmentCountry_add')}
      />
    </>
  );
};

export default Dac7PermanentEstablishment;
